import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign,
  ChevronDown,
  Monitor,
  Server,
  BarChart3,
  FileCheck,
  Shield,
  Bot,
  MessageSquare,
  Image,
  Mic,
  Check,
  X,
} from "lucide-react";

/* ─── Software Stack data (mirrors SoftwareStackCostSlide) ─── */
const softwareItems = [
  { icon: Monitor, name: "Practice Management System", cost: "$400-700", avg: 550, examples: "Dentrix, Eaglesoft, Open Dental" },
  { icon: Server, name: "IT Spend for PMS", cost: "$200-400", avg: 300, examples: "Servers, maintenance, backups" },
  { icon: BarChart3, name: "Dental Intel", cost: "$300-500", avg: 400, examples: "Revenue intelligence & analytics" },
  { icon: FileCheck, name: "eClaims Portals", cost: "$150-250", avg: 200, examples: "Vyne Trellis, DentalXChange" },
  { icon: Shield, name: "Insurance Verification", cost: "$200-400", avg: 300, examples: "Zuub, AirPay, Vyne" },
  { icon: Bot, name: "AI Receptionist", cost: "$300-500", avg: 400, examples: "Dentina, Arini" },
  { icon: MessageSquare, name: "Communication Suite", cost: "$700-1,200", avg: 950, examples: "Weave, Mango, Podium" },
  { icon: Image, name: "Imaging Software", cost: "$200-400", avg: 300, examples: "Dexis, Apteryx, Carestream" },
  { icon: Mic, name: "AI Scribe ($149/mo x2 providers)", cost: "$298", avg: 298, examples: "Typically 2 providers per office" },
];

/* ─── RCM comparison data (mirrors RCMPricingComparisonSlide) ─── */
const rcmComparisonData = [
  { category: "Insurance Billing", eAssist: "3.5% of Collections", wisdom: "3.5% of Collections" },
  { category: "AI RCM Suite", eAssist: "Not Included", wisdom: "Not Included" },
  { category: "Insurance Verification", eAssist: "$8-12/patient", wisdom: "$99/mo + $8.95/patient" },
  { category: "Patient Billing", eAssist: "$925+/mo", wisdom: "$1,097-$1,797/mo" },
  { category: "Payment Reconciliation", eAssist: "$199+/mo", wisdom: "Not Included" },
  { category: "Denial Management", eAssist: "Often Not Included", wisdom: "Included in billing" },
];

/* ─── Front office labor items ─── */
const laborItems = [
  { task: "Schedule Optimization & Confirmation", pct: 20 },
  { task: "Hygiene Recall & Reactivation", pct: 15 },
  { task: "Case Acceptance Follow-Up", pct: 15 },
  { task: "Patient Intake & Forms", pct: 15 },
  { task: "Patient Experience & Reviews", pct: 10 },
  { task: "Fax Management & Filing", pct: 10 },
  { task: "Insurance Calls & Hold Time", pct: 15 },
];

const RcmCell = ({ value }) => {
  if (value === "Not Included" || value === "Often Not Included") {
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
          <X className="w-2.5 h-2.5 text-red-400" />
        </div>
        <span className="text-xs text-slate-400">{value}</span>
      </div>
    );
  }
  if (value === "Included in billing") {
    return (
      <div className="flex items-center gap-1.5">
        <Check className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-xs text-slate-500">{value}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
        <X className="w-2.5 h-2.5 text-red-400" />
      </div>
      <span className="text-xs text-slate-500">{value}</span>
    </div>
  );
};

const TotalCostCalculatorSlide = ({
  initialCollections,
  initialPatients,
  initialProviders,
  quoteMode = false,
}) => {
  const [collections, setCollections] = useState(initialCollections || 150000);
  const [patients, setPatients] = useState(initialPatients || 400);
  const [providers, setProviders] = useState(initialProviders || 2);
  const [expanded, setExpanded] = useState(quoteMode ? 0 : null);

  // RCM costs
  let billingCost = 0;
  if (collections <= 100000) {
    billingCost = collections * 0.035;
  } else if (collections <= 150000) {
    billingCost = 100000 * 0.035 + (collections - 100000) * 0.03;
  } else {
    billingCost =
      100000 * 0.035 + 50000 * 0.03 + (collections - 150000) * 0.025;
  }
  const rcmInsuranceVerif = patients * 8;
  const rcmPatientBilling = 1000;
  const rcmPaymentRec = 200;
  const rcmTotal =
    Math.round(billingCost) + rcmInsuranceVerif + rcmPatientBilling + rcmPaymentRec;

  // Software stack
  const softwareBase = 3698;
  const softwareTotal = softwareBase + (providers - 2) * 149;

  // Front office labor (~$4,500/mo per FTE at ~$27/hr)
  const fteFraction = (collections / 100000) * 0.5;
  const frontOfficeTotal = Math.round(fteFraction * 4500);

  const grandTotal = rcmTotal + softwareTotal + frontOfficeTotal;

  const toggle = (i) => setExpanded(expanded === i ? null : i);

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 lg:p-8 relative overflow-hidden font-sans">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="relative z-10 flex flex-col h-full w-full">
        {/* Header */}
        <div className="mb-3 flex-shrink-0">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              {quoteMode ? (
                <>
                  Based on other vendors' <span className="text-blue-600">published rates</span>, it would cost you{" "}
                  <span className="text-red-500">${grandTotal.toLocaleString()}/mo</span> to piecemeal this OS
                </>
              ) : (
                <>
                  Based on other vendors' <span className="text-blue-600">published rates</span>, what would it cost to piecemeal this OS?
                </>
              )}
            </h2>
          </motion.div>
        </div>

        {/* Calculator Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex-1 bg-white rounded-3xl border border-slate-200/60 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] p-5 flex flex-col min-h-0"
        >
          {/* Sliders */}
          <div className="grid grid-cols-3 gap-6 mb-3 pb-3 border-b border-slate-100 flex-shrink-0">
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Monthly Collections</label>
                <span className="text-lg font-extrabold text-slate-900 tabular-nums">${collections.toLocaleString()}</span>
              </div>
              <input type="range" min="50000" max="500000" step="10000" value={collections}
                onChange={(e) => setCollections(Number(e.target.value))}
                className="w-full h-2 bg-slate-100 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-md" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5"><span>$50K</span><span>$500K</span></div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Patients / Month</label>
                <span className="text-lg font-extrabold text-slate-900 tabular-nums">{patients}</span>
              </div>
              <input type="range" min="100" max="1000" step="25" value={patients}
                onChange={(e) => setPatients(Number(e.target.value))}
                className="w-full h-2 bg-slate-100 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-md" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5"><span>100</span><span>1,000</span></div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Providers</label>
                <span className="text-lg font-extrabold text-slate-900 tabular-nums">{providers}</span>
              </div>
              <input type="range" min="1" max="6" step="1" value={providers}
                onChange={(e) => setProviders(Number(e.target.value))}
                className="w-full h-2 bg-slate-100 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-md" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5"><span>1</span><span>6</span></div>
            </div>
          </div>

          {/* Expandable Breakdown Rows */}
          <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto">

            {/* ── RCM / Billing Vendors ── */}
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <button onClick={() => toggle(0)} className="w-full flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors px-4 py-2.5 text-left">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-bold text-slate-900">RCM / Billing Vendors</span>
                  <p className="text-[11px] text-slate-400 mt-0.5 truncate">eAssist, Wisdom — insurance billing, verification, patient billing, payment reconciliation</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <span className="text-lg font-extrabold text-red-500 tabular-nums">
                    ${rcmTotal.toLocaleString()}<span className="text-[10px] font-medium text-slate-400">/mo</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded === 0 ? "rotate-180" : ""}`} />
                </div>
              </button>
              <AnimatePresence>
                {expanded === 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 py-3 border-t border-slate-100 bg-white">
                      <div className="flex gap-3">
                        {/* Comparison table */}
                        <div className="flex-1 rounded-lg border border-slate-200 overflow-hidden">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[34%]">Service</th>
                                <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[33%]">eAssist</th>
                                <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[33%]">Wisdom</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rcmComparisonData.map((row, i) => (
                                <tr key={i} className="border-b border-slate-100">
                                  <td className="py-1.5 px-2.5 text-[11px] font-semibold text-slate-700">{row.category}</td>
                                  <td className="py-1.5 px-2.5"><RcmCell value={row.eAssist} /></td>
                                  <td className="py-1.5 px-2.5"><RcmCell value={row.wisdom} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Cost breakdown mini card */}
                        <div className="w-[200px] bg-[#0f172a] rounded-lg p-3 text-white flex-shrink-0">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Cost Breakdown</p>
                          <div className="space-y-1">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-400">Insurance Billing (3.5%)</span>
                              <span className="font-semibold">${Math.round(billingCost).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-400">Insurance Verif ($8/pt)</span>
                              <span className="font-semibold">${rcmInsuranceVerif.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-400">Patient Billing</span>
                              <span className="font-semibold">${rcmPatientBilling.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-400">Payment Reconciliation</span>
                              <span className="font-semibold">${rcmPaymentRec.toLocaleString()}</span>
                            </div>
                            <div className="h-px bg-white/10 my-0.5" />
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-300 font-bold">Total</span>
                              <span className="font-extrabold text-red-400">${rcmTotal.toLocaleString()}/mo</span>
                            </div>
                          </div>
                          <p className="text-[8px] text-slate-500 mt-1.5">First $100K at 3.5% | $100-150K at 3% | $150K+ at 2.5%</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ── Software Stack ── */}
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <button onClick={() => toggle(1)} className="w-full flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors px-4 py-2.5 text-left">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-bold text-slate-900">Software Stack (8-10 tools)</span>
                  <p className="text-[11px] text-slate-400 mt-0.5 truncate">PMS, IT, Dental Intel, eClaims, insurance verif, AI receptionist, comms suite, imaging, AI scribe</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <span className="text-lg font-extrabold text-blue-600 tabular-nums">
                    ${softwareTotal.toLocaleString()}<span className="text-[10px] font-medium text-slate-400">/mo</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded === 1 ? "rotate-180" : ""}`} />
                </div>
              </button>
              <AnimatePresence>
                {expanded === 1 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 py-3 border-t border-slate-100 bg-white">
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                              <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[45%]">Software</th>
                              <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[35%]">Common Vendors</th>
                              <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[20%] text-right">Typical Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {softwareItems.map((item, i) => {
                              const Icon = item.icon;
                              return (
                                <tr key={i} className="border-b border-slate-100">
                                  <td className="py-1.5 px-2.5">
                                    <div className="flex items-center gap-1.5">
                                      <Icon className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                      <span className="text-[11px] font-semibold text-slate-800">{item.name}</span>
                                    </div>
                                  </td>
                                  <td className="py-1.5 px-2.5 text-[11px] text-slate-400">{item.examples}</td>
                                  <td className="py-1.5 px-2.5 text-right">
                                    <span className="text-[11px] font-bold text-red-500">{item.cost}<span className="text-[9px] font-medium text-slate-400">/mo</span></span>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="bg-slate-50/80">
                              <td className="py-1.5 px-2.5"><span className="text-[11px] font-extrabold text-slate-900">Total</span></td>
                              <td className="py-1.5 px-2.5 text-[11px] text-slate-400">Across 8-10 separate vendors</td>
                              <td className="py-1.5 px-2.5 text-right">
                                <span className="text-[11px] font-extrabold text-red-500">~${softwareItems.reduce((a, b) => a + b.avg, 0).toLocaleString()}<span className="text-[9px] font-medium text-red-300">/mo</span></span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ── Front Office Staff Labor ── */}
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <button onClick={() => toggle(2)} className="w-full flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors px-4 py-2.5 text-left">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-bold text-slate-900">Front Office Staff Labor</span>
                  <p className="text-[11px] text-slate-400 mt-0.5 truncate">Schedule optimization, hygiene recall, case acceptance, intake, patient experience, fax management</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <span className="text-lg font-extrabold text-purple-600 tabular-nums">
                    ${frontOfficeTotal.toLocaleString()}<span className="text-[10px] font-medium text-slate-400">/mo</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded === 2 ? "rotate-180" : ""}`} />
                </div>
              </button>
              <AnimatePresence>
                {expanded === 2 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 py-3 border-t border-slate-100 bg-white">
                      <div className="flex gap-3">
                        <div className="flex-1 rounded-lg border border-slate-200 overflow-hidden">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[70%]">Task Asha AI Automates</th>
                                <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[15%] text-right">% of Time</th>
                                <th className="text-[9px] uppercase tracking-wider font-bold text-slate-400 py-1.5 px-2.5 w-[15%] text-right">Est. Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {laborItems.map((item, i) => (
                                <tr key={i} className="border-b border-slate-100">
                                  <td className="py-1.5 px-2.5 text-[11px] font-semibold text-slate-700">{item.task}</td>
                                  <td className="py-1.5 px-2.5 text-[11px] text-slate-500 text-right">{item.pct}%</td>
                                  <td className="py-1.5 px-2.5 text-[11px] font-bold text-purple-600 text-right">
                                    ${Math.round(frontOfficeTotal * item.pct / 100).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                              <tr className="bg-slate-50/80">
                                <td className="py-1.5 px-2.5"><span className="text-[11px] font-extrabold text-slate-900">Total (~{fteFraction.toFixed(1)} FTE)</span></td>
                                <td className="py-1.5 px-2.5 text-[11px] text-slate-500 text-right">100%</td>
                                <td className="py-1.5 px-2.5 text-right">
                                  <span className="text-[11px] font-extrabold text-purple-600">${frontOfficeTotal.toLocaleString()}<span className="text-[9px] font-medium text-purple-300">/mo</span></span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="w-[180px] bg-purple-50 rounded-lg border border-purple-100 p-3 flex-shrink-0">
                          <p className="text-[9px] font-bold text-purple-400 uppercase tracking-wider mb-1.5">How We Calculate</p>
                          <p className="text-[11px] text-slate-600 leading-snug">
                            ~0.5 FTE per $100K collections at ~$4,500/mo per employee (~$27/hr).
                          </p>
                          <div className="mt-2 pt-2 border-t border-purple-200">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-slate-500">Collections</span>
                              <span className="font-bold text-slate-700">${collections.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-[11px] mt-0.5">
                              <span className="text-slate-500">FTE needed</span>
                              <span className="font-bold text-slate-700">{fteFraction.toFixed(1)}</span>
                            </div>
                            <div className="flex justify-between text-[11px] mt-0.5">
                              <span className="text-slate-500">Monthly cost</span>
                              <span className="font-bold text-purple-600">${frontOfficeTotal.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Total */}
          {!quoteMode && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-2 bg-white rounded-xl border-2 border-red-200 shadow-lg px-5 py-3 flex items-center justify-between flex-shrink-0"
            >
              <div>
                <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider block mb-0.5">
                  Total Monthly Cost to Piecemeal
                </span>
                <span className="text-[10px] text-slate-400">
                  Based on published rates from eAssist, Wisdom, Dentina, Weave, Dental Intel, and others
                </span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-3xl font-extrabold text-red-500 tracking-tight tabular-nums">
                  ${grandTotal.toLocaleString()}
                </span>
                <span className="text-sm text-red-300 font-medium">/mo</span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default TotalCostCalculatorSlide;
